/**
 * Product page upsell schema contract (layout family: product-page-upsell-v1).
 * Shared by editor, preview, validation, and server tests.
 */
import { POPUP_LAYOUT_IDS } from "./popup-layouts/registry.js";
import {
  getProductPageSlotNo,
  isProductPageSlotMode,
  validateProductPageSlotConstraints,
} from "./product-page-slot.js";

export const PRODUCT_PAGE_UPSELL_TEMPLATE_TYPE = "Product Page Upsell";

export const STATIC_PRODUCT_PAGE_UPSELL_SECTION_INDEX = Object.freeze({
  UPSELL_HEADING: 0,
  UPSELL_PRODUCT: 1,
  BRAND_ATTRIBUTION: 2,
});

export const STATIC_PRODUCT_PAGE_UPSELL_SECTION_COUNT = 3;

/** Expected section.type per fixed slot (editor + preview indices). */
export const STATIC_PRODUCT_PAGE_UPSELL_SECTION_TYPES = Object.freeze([
  "TITLE",
  "PRODUCT",
  "BRAND_ATTRIBUTION",
]);

export function isStaticProductPageUpsellContentStep(step) {
  if (!step || typeof step !== "object") return false;
  if (step.productPageUpsellLayout) return true;
  const sections = step.section ?? step.sections ?? [];
  if (
    sections.length >= STATIC_PRODUCT_PAGE_UPSELL_SECTION_COUNT &&
    sections[STATIC_PRODUCT_PAGE_UPSELL_SECTION_INDEX.UPSELL_PRODUCT]?.type ===
      "PRODUCT"
  ) {
    const heading = sections[STATIC_PRODUCT_PAGE_UPSELL_SECTION_INDEX.UPSELL_HEADING];
    const brand =
      sections[STATIC_PRODUCT_PAGE_UPSELL_SECTION_INDEX.BRAND_ATTRIBUTION];
    return (
      heading?.type === "TITLE" &&
      (brand?.type === "BRAND_ATTRIBUTION" ||
        brand?.setting?.role === "brandAttribution")
    );
  }
  return sections.some(
    (s) => s?.setting?.productPagePart === "upsell_product"
  );
}

function validateProductPageUpsellSections(sections, errors, prefix) {
  if (!Array.isArray(sections)) {
    errors.push(`${prefix}: section must be an array`);
    return;
  }
  if (sections.length < STATIC_PRODUCT_PAGE_UPSELL_SECTION_COUNT) {
    errors.push(
      `${prefix}: expected ${STATIC_PRODUCT_PAGE_UPSELL_SECTION_COUNT} sections, got ${sections.length}`
    );
    return;
  }

  STATIC_PRODUCT_PAGE_UPSELL_SECTION_TYPES.forEach((expectedType, index) => {
    const section = sections[index];
    if (!section || section.type !== expectedType) {
      errors.push(
        `${prefix}: slot ${index} expected type ${expectedType}, got ${section?.type ?? "missing"}`
      );
    }
  });

  if (
    sections[STATIC_PRODUCT_PAGE_UPSELL_SECTION_INDEX.UPSELL_HEADING]?.setting
      ?.productPagePart !== "upsell_heading"
  ) {
    errors.push(`${prefix}: heading slot must set productPagePart upsell_heading`);
  }
  if (
    sections[STATIC_PRODUCT_PAGE_UPSELL_SECTION_INDEX.UPSELL_PRODUCT]?.setting
      ?.productPagePart !== "upsell_product"
  ) {
    errors.push(`${prefix}: product slot must set productPagePart upsell_product`);
  }
}

/**
 * Validate popup payload for static product page upsell.
 * @param {object} popup
 * @returns {{ ok: boolean, errors: string[] }}
 */
export function validateStaticProductPageUpsellPopup(popup) {
  const errors = [];

  if (!popup || typeof popup !== "object") {
    return { ok: false, errors: ["popup is required"] };
  }

  const gs = popup.generalSettings || {};
  if (gs.isStaticProductPageUpsell !== true) {
    errors.push("generalSettings.isStaticProductPageUpsell must be true");
  }
  if (gs.isDraggable !== false) {
    errors.push("generalSettings.isDraggable must be false");
  }
  const layoutId = gs.popupLayoutId;
  if (layoutId && layoutId !== POPUP_LAYOUT_IDS.PRODUCT_PAGE_UPSELL) {
    errors.push(
      `generalSettings.popupLayoutId must be ${POPUP_LAYOUT_IDS.PRODUCT_PAGE_UPSELL} (got ${layoutId})`
    );
  }

  const popUpType = popup.popUpType || popup.template_type;
  if (popUpType && popUpType !== PRODUCT_PAGE_UPSELL_TEMPLATE_TYPE) {
    errors.push(`popUpType must be ${PRODUCT_PAGE_UPSELL_TEMPLATE_TYPE}`);
  }

  const schema = popup.schema;
  if (!Array.isArray(schema) || schema.length === 0) {
    errors.push("schema must be a non-empty array");
    return { ok: false, errors };
  }

  let contentIndex = null;
  schema.forEach((step, index) => {
    if (isStaticProductPageUpsellContentStep(step) && contentIndex == null) {
      contentIndex = index;
    }
  });

  schema.forEach((step, index) => {
    if (isStaticProductPageUpsellContentStep(step)) {
      const sections = step.section ?? step.sections ?? [];
      validateProductPageUpsellSections(sections, errors, `schema[${index}]`);
      if (!step.productPageUpsellLayout) {
        errors.push(`schema[${index}]: productPageUpsellLayout required`);
      }
    }
  });

  if (contentIndex == null) {
    errors.push("schema must include one product page upsell content step");
  }

  if (isProductPageSlotMode(popup)) {
    const slotValidation = validateProductPageSlotConstraints(popup, {
      peers: [],
      requireSlot: true,
    });
    if (!slotValidation.ok) {
      errors.push(...slotValidation.errors);
    } else if (!getProductPageSlotNo(popup)) {
      errors.push("generalSettings.condition.embed.slot_no must be 1 or 2");
    }
  }

  return { ok: errors.length === 0, errors };
}
